/**
 * BrandLogo — the only UI entry for the BuzzBit X product mark.
 *
 * `lockup` (default) is the full wordmark + bee + growth mark + tagline.
 * `mark` is the square favicon glyph, for tight chrome (app icon, push).
 * Callers wrap in <Link> when the logo should navigate.
 *
 * logo.png is 1024² with lockup ink ~901×436 on an opaque white canvas.
 * A plain height cap + object-contain therefore shows a tiny wordmark —
 * the same crop used on the landing Navbar/Footer. Change LOCKUP_PX /
 * MARK_PX here — not at call sites.
 */
import React from 'react';
import { cn } from '@/lib/utils';
import { BRAND_FAVICON_SRC, BRAND_LOGO_SRC } from '@/config/brand';
import { t } from '@/i18n';

export type BrandLogoSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type BrandLogoVariant = 'lockup' | 'mark';

interface LogoPx {
  width: number;
  height: number;
}

/** Visible box for the cropped lockup (ink, not the padded square file). */
const LOCKUP_PX: Record<BrandLogoSize, LogoPx> = {
  /** Compact chrome (sidebar header aligned with TopBar h-16). */
  xs: { width: 132, height: 56 },
  sm: { width: 160, height: 72 },
  md: { width: 200, height: 88 },
  lg: { width: 240, height: 104 },
  xl: { width: 280, height: 120 },
};

const MARK_PX: Record<BrandLogoSize, LogoPx> = {
  xs: { width: 28, height: 28 },
  sm: { width: 36, height: 36 },
  md: { width: 48, height: 48 },
  lg: { width: 60, height: 60 },
  xl: { width: 80, height: 80 },
};

export type BrandLogoAlign = 'start' | 'center';

interface BrandLogoProps {
  size?: BrandLogoSize;
  variant?: BrandLogoVariant;
  /** `center` for standalone form pages; `start` for left chrome (sidebar). */
  align?: BrandLogoAlign;
  className?: string;
  /** Override the catalog alt; defaults to t('brand.logoAlt'). */
  alt?: string;
}

export function BrandLogo({
  size = 'md',
  variant = 'lockup',
  align = 'start',
  className,
  alt,
}: BrandLogoProps): React.ReactElement {
  const label = alt ?? t('brand.logoAlt');
  const centered = align === 'center';

  if (variant === 'mark') {
    const { width, height } = MARK_PX[size];
    return (
      <img
        src={BRAND_FAVICON_SRC}
        alt={label}
        width={width}
        height={height}
        style={{ width: `${width}px`, height: `${height}px`, maxWidth: '100%' }}
        className={cn(
          'max-w-full rounded-md object-contain',
          centered ? 'mx-auto object-center' : 'object-left',
          className,
        )}
        loading="eager"
        decoding="async"
        draggable={false}
      />
    );
  }

  const { width, height } = LOCKUP_PX[size];
  return (
    <span
      className={cn(
        'relative block shrink-0 overflow-hidden',
        centered ? 'mx-auto' : undefined,
        className,
      )}
      style={{ width: `${width}px`, height: `${height}px`, maxWidth: '100%' }}
      data-testid="brand-logo-lockup"
      data-size={size}
    >
      <img
        src={BRAND_LOGO_SRC}
        alt={label}
        width={width}
        height={height}
        className="pointer-events-none absolute left-1/2 top-[45%] h-[220%] w-[220%] max-w-none -translate-x-1/2 -translate-y-1/2 object-contain"
        loading="eager"
        decoding="async"
        draggable={false}
      />
    </span>
  );
}

BrandLogo.displayName = 'BrandLogo';
